// CountrySelect — country dropdown for the second step of opt-in forms.
// Self-prefills from GET /api/public/geo (Cloudflare IP country) so the visitor's
// country is selected by default but always changeable — so most visitors never
// have to touch this control at all before the parent form's own required-field
// gate is satisfied. Style-flexible (takes a `style` prop) so it drops into each
// form's look. Posts an ISO-2 code as `country`. Whether country is actually
// required is enforced by the CONSUMING form (its submit-disabled condition),
// not here — the placeholder text stays neutral rather than claiming "optional",
// since as of 2026-09-13 that's no longer true for most consumers.

import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';

// Curated list — the TSB/PB audience plus the major English-speaking + large
// markets. Value is ISO-3166-1 alpha-2. "Other" lets anyone else self-identify.
const COUNTRIES: Array<{ code: string; name: string }> = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'IE', name: 'Ireland' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'IN', name: 'India' },
  { code: 'SG', name: 'Singapore' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'PH', name: 'Philippines' },
  { code: 'MX', name: 'Mexico' },
  { code: 'BR', name: 'Brazil' },
  { code: 'OTHER', name: 'Other' },
];

export function CountrySelect({
  value, onChange, style, className, id, disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  style?: CSSProperties;
  className?: string;
  id?: string;
  disabled?: boolean;
}) {
  // Prefill from IP once, only if the visitor hasn't chosen anything yet.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || value) return;
    prefilled.current = true;
    let cancelled = false;
    fetch(`${API_BASE}/api/public/geo`)
      .then((r) => r.json())
      .then((d: { country?: string | null }) => {
        const cc = (d?.country ?? '').toUpperCase();
        if (cancelled || !cc) return;
        // Only prefill if it's a country we list (else leave blank rather than guess "Other").
        if (COUNTRIES.some((c) => c.code === cc)) onChange(cc);
      })
      .catch(() => { /* best-effort prefill */ });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <select id={id} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} style={style} className={className} aria-label="Country">
      <option value="">Country</option>
      {COUNTRIES.map((c) => (
        <option key={c.code} value={c.code}>{c.name}</option>
      ))}
    </select>
  );
}
